import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { CircularProgress } from '@mui/material';

import { AdminDashboardApi } from '@app/domain/dashboard/api/AdminDashboardApi';
import { clsxm } from '@app/styles/clsxm';

import { DashboardCard } from '../shared/DashboardCard';
import { useChartTable } from '../shared/useChartTable';
import { DASHBOARD_COLOR } from '../shared/dashboardColors';

const fmt = (n: number) => n.toLocaleString('nl-NL');
const dayLabel = (iso: string) => format(parseISO(iso), 'EEEEEE d', { locale: nl });
const weekLabel = (iso: string) => `wk ${format(parseISO(iso), 'I', { locale: nl })}`;

// admin-dashboard-opdracht.md §4.6 - built second in the backend's own bouwvolgorde ("de band
// waar Danny vandaag iets mee kán"). Track C bottom, B middle, A top (opdracht's exact order).
export const DashboardUpcomingBand: React.FC = () => {
  const query = useQuery(['admin-dashboard-upcoming'], () => AdminDashboardApi.getUpcoming());
  const trials = useChartTable();

  if (query.isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!query.data) {
    return null;
  }

  const { expiringTrials, renewals, scheduledCancellations, nearScanLimit } = query.data;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <DashboardCard
          title="Proefperiodes die aflopen"
          subtitle="Komende 7 dagen, per track"
          headerRight={trials.toggleButton}
        >
          <div className="mb-2 flex flex-wrap gap-4 text-[12.5px] text-gray-600">
            <Legend color={DASHBOARD_COLOR.cat1} label="Track C — scant actief" />
            <Legend color={DASHBOARD_COLOR.cat2} label="Track B — code, weinig scans" />
            <Legend color={DASHBOARD_COLOR.cat3} label="Track A — geen code" />
          </div>
          {trials.showTable ? (
            <TrialsTable rows={expiringTrials} />
          ) : (
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expiringTrials} margin={{ top: 8, right: 8 }}>
                  <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={dayLabel}
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                    stroke={DASHBOARD_COLOR.muted}
                  />
                  <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} width={28} />
                  <RTooltip
                    cursor={false}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const row = payload[0].payload as typeof expiringTrials[number];
                      return (
                        <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] leading-relaxed text-white">
                          <b>{dayLabel(label as string)}</b>
                          <br />
                          Track C: <b>{row.trackC}</b> · Track B: <b>{row.trackB}</b> · Track A: <b>{row.trackA}</b>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="trackC" stackId="t" fill={DASHBOARD_COLOR.cat1} />
                  <Bar dataKey="trackB" stackId="t" fill={DASHBOARD_COLOR.cat2} />
                  <Bar dataKey="trackA" stackId="t" fill={DASHBOARD_COLOR.cat3} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-2 text-[11.5px] text-gray-400">
            Track C die overmorgen afloopt is een warme kans. Track A is verloren moeite - dat onderscheid is de hele
            reden dat deze staven gestapeld zijn.
          </p>
        </DashboardCard>

        <DashboardCard title="Verlengingen" subtitle="Komende 30 dagen, per week">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={renewals} margin={{ top: 8, right: 8 }}>
                <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                <XAxis
                  dataKey="weekStart"
                  tickFormatter={weekLabel}
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  stroke={DASHBOARD_COLOR.muted}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} width={28} />
                <RTooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const row = payload[0].payload as typeof renewals[number];
                    return (
                      <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-white">
                        <b>{weekLabel(label as string)}</b>
                        {row.running && <span className="opacity-70"> (loopt nog)</span>}
                        <br />
                        Verlengingen: <b>{fmt(row.value)}</b>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {renewals.map((r) => (
                    <Cell key={r.weekStart} fill={r.running ? DASHBOARD_COLOR.running : DASHBOARD_COLOR.cat2} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11.5px] text-gray-400">Aantallen, geen bedragen - zie de opmerking bij Geld.</p>
        </DashboardCard>
      </div>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <DashboardCard
          title="Al ingeplande opzeggingen"
          subtitle="Loopt af, maar nog niet verstreken - hier kun je nog iets doen"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-gray-400">
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Klant</th>
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Plan</th>
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Loopt af</th>
                  <th className="py-2 text-right font-semibold uppercase tracking-wide">Scans/mnd</th>
                </tr>
              </thead>
              <tbody>
                {scheduledCancellations.map((row, i) => (
                  <tr key={i} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
                    <td className="py-1.5 pr-3">{row.maskedEmail}</td>
                    <td className="py-1.5 pr-3">{row.plan}</td>
                    <td className="py-1.5 pr-3">{format(parseISO(row.expiresOn), 'd MMM', { locale: nl })}</td>
                    <td className="py-1.5 text-right tabular-nums">{fmt(row.monthlyScans)}</td>
                  </tr>
                ))}
                {!scheduledCancellations.length && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-gray-400">
                      Geen geplande opzeggingen.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DashboardCard>

        <DashboardCard
          title="Tegen de scanlimiet aan"
          subtitle="Boven 80% van de maandlimiet - je concreetste upgradesignaal"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-gray-400">
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Klant</th>
                  <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Plan</th>
                  <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Verbruik</th>
                  <th className="py-2 font-semibold uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {nearScanLimit.map((row, i) => (
                  <tr key={i} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
                    <td className="py-1.5 pr-3">{row.maskedEmail}</td>
                    <td className="py-1.5 pr-3">{row.plan}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {fmt(row.monthlyScans)} / {fmt(row.scanLimit)}
                    </td>
                    <td className="py-1.5">
                      {row.overLimit ? (
                        <StatusChip tone="err">Over de limiet</StatusChip>
                      ) : (
                        <StatusChip tone="warn">{row.percentUsed.toFixed(0)}%</StatusChip>
                      )}
                    </td>
                  </tr>
                ))}
                {!nearScanLimit.length && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-gray-400">
                      Niemand boven 80% van de scanlimiet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11.5px] text-gray-400">
            Let op de noemer: proefaccounts hebben een andere limiet dan betaalde.
          </p>
        </DashboardCard>
      </div>
    </div>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: color }} />
    {label}
  </span>
);

const StatusChip: React.FC<{ tone: 'err' | 'warn'; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={clsxm(
      'inline-block rounded px-1.5 py-0.5 text-[11.5px] font-medium',
      tone === 'err' ? 'bg-[#fde8e8] text-[#9b3634]' : 'bg-[#fdf4d0] text-[#7a6510]'
    )}
  >
    {children}
  </span>
);

const TrialsTable: React.FC<{
  rows: { day: string; trackC: number; trackB: number; trackA: number }[];
}> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-gray-200 text-gray-400">
          <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Dag</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Track C</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Track B</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Track A</th>
          <th className="py-2 text-right font-semibold uppercase tracking-wide">Totaal</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.day} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
            <td className="py-1.5 pr-3">{dayLabel(r.day)}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.trackC}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.trackB}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.trackA}</td>
            <td className="py-1.5 text-right tabular-nums">{r.trackC + r.trackB + r.trackA}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
